import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  listManagementActivities,
  listOrgUnits,
  listActivityAreas,
  listActivityScopeItems,
  getOrgFacets,
  getActivityCounts,
  type ActivityQuery,
  type OrgUnitQuery,
} from "@/api/management";
import { stageKeys } from "@/hooks/useConfigServerStages";
import {
  LIST_PAGE_SIZE,
  PANEL_PAGE_SIZE,
  LIST_QUERY_OPTIONS,
} from "@/lib/config-pagination";

export const managementKeys = {
  all: (projectId: string) =>
    [...stageKeys.all(projectId), "management"] as const,
  orgUnits: (
    projectId: string,
    artifactId: string | undefined,
    query: OrgUnitQuery,
    mode: string,
  ) =>
    [
      ...stageKeys.all(projectId),
      "management",
      "org-units",
      artifactId ?? "",
      mode,
      query,
    ] as const,
  activities: (
    projectId: string,
    artifactId: string | undefined,
    query: ActivityQuery,
    mode: string,
  ) =>
    [
      ...stageKeys.all(projectId),
      "management",
      "activities",
      artifactId ?? "",
      mode,
      query,
    ] as const,
  preview: (
    projectId: string,
    artifactId: string | undefined,
    operations: unknown,
  ) =>
    [
      ...stageKeys.all(projectId),
      "management",
      "preview",
      artifactId ?? "",
      operations,
    ] as const,
};
export function useOrgUnitsScroll(
  projectId: string,
  artifactId: string | undefined,
  query: OrgUnitQuery,
  enabled = true,
  limit = LIST_PAGE_SIZE,
) {
  return useInfiniteQuery({
    queryKey: managementKeys.orgUnits(
      projectId,
      artifactId,
      query,
      `scroll-${limit}`,
    ),
    queryFn: ({ pageParam }) =>
      listOrgUnits(projectId, query, { cursor: pageParam, limit }, artifactId),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
    ...LIST_QUERY_OPTIONS,
  });
}
/** Leitura limitada para a contagem de unidades manuais, sem carregar a árvore. */
export function useOrgUnitsPage(
  projectId: string,
  artifactId: string | undefined,
  query: OrgUnitQuery,
  _page: number,
  enabled = true,
) {
  return useQuery({
    queryKey: managementKeys.orgUnits(projectId, artifactId, query, "summary"),
    queryFn: () =>
      listOrgUnits(projectId, query, { limit: PANEL_PAGE_SIZE }, artifactId),
    enabled,
    ...LIST_QUERY_OPTIONS,
  });
}
export function useOrgFacets(
  projectId: string,
  artifactId: string | undefined,
) {
  const result = useQuery({
    queryKey: [...managementKeys.all(projectId), "org-facets", artifactId],
    queryFn: () => getOrgFacets(projectId, artifactId),
    ...LIST_QUERY_OPTIONS,
  });
  return {
    unitTypes: result.data?.unit_types ?? [],
    countries: result.data?.countries ?? [],
  };
}
export function useActivityCounts(projectId: string) {
  return useQuery({
    queryKey: [...managementKeys.all(projectId), "counts"],
    queryFn: () => getActivityCounts(projectId),
    ...LIST_QUERY_OPTIONS,
  });
}
export function useActivityAreas(
  projectId: string,
  artifactId: string | undefined,
  query: ActivityQuery,
  enabled = true,
) {
  return useQuery({
    queryKey: managementKeys.activities(projectId, artifactId, query, "areas"),
    queryFn: () => listActivityAreas(projectId, query, artifactId),
    enabled,
    ...LIST_QUERY_OPTIONS,
  });
}
export function useActivityScopeItems(
  projectId: string,
  artifactId: string | undefined,
  query: ActivityQuery,
  enabled: boolean,
  limit = LIST_PAGE_SIZE,
) {
  return useInfiniteQuery({
    queryKey: managementKeys.activities(
      projectId,
      artifactId,
      query,
      `scopes-${limit}`,
    ),
    queryFn: ({ pageParam }) =>
      listActivityScopeItems(
        projectId,
        query,
        { cursor: pageParam, limit },
        artifactId,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
    ...LIST_QUERY_OPTIONS,
  });
}
export function useActivitiesScroll(
  projectId: string,
  artifactId: string | undefined,
  query: ActivityQuery,
  enabled = true,
  limit = LIST_PAGE_SIZE,
) {
  return useInfiniteQuery({
    queryKey: managementKeys.activities(
      projectId,
      artifactId,
      query,
      `scroll-${limit}`,
    ),
    queryFn: ({ pageParam }) =>
      listManagementActivities(
        projectId,
        query,
        { cursor: pageParam, limit },
        artifactId,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
    ...LIST_QUERY_OPTIONS,
  });
}
